import { iOrder } from "core/kangarooTypes/iOrder";
import { iPayment } from "core/kangarooTypes/iPayment";
import { getUsers } from "core/Services/AdminServices";
import {
  downloadPaymentsExcel,
  getOrders,
  getPayments,
} from "core/Services/MerchantServices";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Download,
  Search,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import Button from "shared/Button";
import FormInput from "shared/FormInput";
import { formatDate, formatStatus } from "utils/DatesUtil";
import { getStatusColors } from "utils/statusColors";
import { useTranslation } from "react-i18next";

const AccountantDashboard = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [orders, setOrders] = useState<any>();
  const [payouts, setPayouts] = useState<any>();
  const [currentPage, setCurrentPage] = useState(1);
  const [merchants, setMerchants] = useState<any[]>([]);
  const [currentFilters, setCurrentFilters] = useState<{
    dateRange?: string;
    merchantId?: string;
    search?: string;
    multiple_status?: string[];
    sortField?: string;
    sortDir?: "asc" | "desc";
  }>({});

  const [sortConfig, setSortConfig] = useState({
    sortField: "created_at",
    sortDir: "desc" as "asc" | "desc",
  });

  const { register, watch } = useForm({
    defaultValues: {
      dateRange: "",
      merchantId: "",
      status: [""],
      order: "",
      sortField: "created_at",
      sortDir: "desc" as "asc" | "desc",
    },
  });

  const calculateDateRange = (days: number) => {
    const dateTo = new Date();
    const dateFrom = new Date();
    dateFrom.setDate(dateTo.getDate() - days);

    const formatDate = (date: Date) => {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    return {
      dateFrom: formatDate(dateFrom),
      dateTo: formatDate(dateTo),
    };
  };

  const dateRangeOptions = [
    {
      value: "7_days",
      label: t("accountantDashboard.filters.dateRangeOptions.last7Days"),
      ...calculateDateRange(7),
    },
    {
      value: "14_days",
      label: t("accountantDashboard.filters.dateRangeOptions.last14Days"),
      ...calculateDateRange(14),
    },
    {
      value: "1_month",
      label: t("accountantDashboard.filters.dateRangeOptions.last1Month"),
      ...calculateDateRange(30),
    },
    {
      value: "3_months",
      label: t("accountantDashboard.filters.dateRangeOptions.last3Months"),
      ...calculateDateRange(90),
    },
    {
      value: "6_months",
      label: t("accountantDashboard.filters.dateRangeOptions.last6Months"),
      ...calculateDateRange(180),
    },
  ];

  // Fulfillment workflow statuses
  const statusOptions = [
    {
      value: "ready_for_packaging",
      label: t("accountantDashboard.filters.statusOptions.readyForPackaging"),
    },
    {
      value: "on_hold",
      label: t("accountantDashboard.filters.statusOptions.onHold"),
    },
    {
      value: "ready_for_shipping",
      label: t("accountantDashboard.filters.statusOptions.readyForShipping"),
    },
    {
      value: "shipped",
      label: t("accountantDashboard.filters.statusOptions.shipped"),
    },
    {
      value: "ship_partially",
      label: t("accountantDashboard.filters.statusOptions.shipPartially"),
    },
    {
      value: "handed_off",
      label: t("accountantDashboard.filters.statusOptions.handedOff"),
    },
    {
      value: "canceled",
      label: t("accountantDashboard.filters.statusOptions.canceled"),
    },
  ];

  useEffect(() => {
    const fetchMerchants = async () => {
      try {
        const response = await getUsers({ role: "merchant", getAll: 1 });
        setMerchants(response.data);
      } catch (error) {
        toast.error("Failed to load merchants.");
      }
    };
    fetchMerchants();
  }, []);

  const fetchData = async (
    filters?: {
      dateRange?: string;
      merchantId?: string;
      search?: string;
      multiple_status?: string[];
      sortField?: string;
      sortDir?: "asc" | "desc";
    },
    page: number = 1
  ) => {
    try {
      setIsLoading(true);
      const selectedRange = dateRangeOptions.find(
        (option) => option.value === filters?.dateRange
      );
      if (filters) {
        setCurrentFilters(filters);
      }

      const response = await getOrders(
        {
          dateFrom: selectedRange?.dateFrom,
          dateTo: selectedRange?.dateTo,
          merchant_id: filters?.merchantId,
          order: filters?.search,
          multiple_status: filters?.multiple_status,
          sort_field: filters?.sortField,
          sort_dir: filters?.sortDir,
        },
        page
      );
      const payments = await getPayments(
        {
          dateFrom: selectedRange?.dateFrom,
          dateTo: selectedRange?.dateTo,
          merchant_id: filters?.merchantId,
          order: filters?.search,
        },
        page
      );
      setOrders(response);
      setPayouts(payments);
    } catch (error) {
      toast.error("Failed to load data.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const subscription = watch((values) => {
      fetchData(
        {
          dateRange: values.dateRange || undefined,
          merchantId: values.merchantId || undefined,
          multiple_status: values.status?.filter(Boolean).length
            ? values.status.filter(
                (status): status is string => status !== undefined
              )
            : undefined,
          search: values.order || undefined,
          sortField: values.sortField,
          sortDir: values.sortDir,
        },
        1
      );
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    fetchData({}, currentPage);
  }, [currentPage]);
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= (orders?.meta.last_page || 1)) {
      setCurrentPage(page);
      fetchData(currentFilters, page);
    }
  };

  const handleSort = (field: string) => {
    setSortConfig((prev) => ({
      sortField: field,
      sortDir:
        prev.sortField === field && prev.sortDir === "asc" ? "desc" : "asc",
    }));

    fetchData(
      {
        ...currentFilters,
        sortField: field,
        sortDir:
          field === sortConfig.sortField && sortConfig.sortDir === "asc"
            ? "desc"
            : "asc",
      },
      currentPage
    );
  };

  const SortHeader = ({ field, label }: { field: string; label: string }) => {
    const isActive = sortConfig.sortField === field;
    return (
      <th
        onClick={() => handleSort(field)}
        className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
      >
        <div className="flex items-center space-x-1">
          <span>{label}</span>
          <div className="flex flex-col">
            {isActive && sortConfig.sortDir === "asc" ? (
              <ArrowUp size={14} />
            ) : null}
            {isActive && sortConfig.sortDir === "desc" ? (
              <ArrowDown size={14} />
            ) : null}
            {!isActive ? (
              <ArrowDown size={14} className="text-gray-300" />
            ) : null}
          </div>
        </div>
      </th>
    );
  };

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Action Buttons */}
      <div className="flex justify-end">
        <Button icon={<Download size={16} />} onClick={downloadPaymentsExcel}>
          {t("accountantDashboard.export")}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {isLoading
          ? [...Array(5)].map((_, index) => (
              <div key={index} className="bg-white p-4 rounded-lg border">
                <Skeleton width={80} />
                <Skeleton width={40} />
              </div>
            ))
          : [
              {
                label: t("accountantDashboard.summaryCards.codTotal"),
                value: payouts?.cod,
              },
              {
                label: t("accountantDashboard.summaryCards.deliveryFees"),
                value: payouts?.delevery_fees,
              },
              {
                label: t("accountantDashboard.summaryCards.totalBalance"),
                value: payouts?.total,
              },
              {
                label: t("accountantDashboard.summaryCards.paymentsProcessed"),
                value: payouts?.payments,
              },
              {
                label: t("accountantDashboard.summaryCards.outstandingBalance"),
                value: payouts?.outstanding,
              },
            ].map((card, index) => (
              <div key={index} className="bg-white p-4 rounded-lg border">
                <div className="text-gray-600 text-sm">{card.label}</div>{" "}
                <div className="text-2xl mt-1">
                  JOD{" "}
                  {card.value?.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }) || "0.00"}
                </div>
              </div>
            ))}
      </div>

      {/* Main Content */}
      <div className="lg:flex lg:space-x-6 rtl:space-x-reverse">
        {/* Filter Section */}
        <div className="bg-white rounded-lg border p-4 space-y-4 lg:w-1/4 h-fit mb-6 lg:mb-0">
          <FormInput
            name="order"
            type="text"
            leftIcon={<Search size={20} />}
            placeholder={t("accountantDashboard.filters.searchOrders")}
            register={register}
            errors={{}}
          />
          <FormInput
            name="dateRange"
            type="select"
            icon={<ChevronDown size={20} />}
            placeholder={t("accountantDashboard.filters.selectDateRange")}
            register={register}
            selectOptions={dateRangeOptions}
            errors={{}}
          />
          <FormInput
            name="merchantId"
            type="select"
            icon={<ChevronDown size={20} />}
            placeholder={t("accountantDashboard.filters.selectMerchant")}
            register={register}
            selectOptions={merchants.map((merchant) => ({
              value: merchant.id,
              label: merchant.business_name,
            }))}
            errors={{}}
          />
          <div>
            <div className="text-sm font-medium mb-3">
              {t("accountantDashboard.filters.status")}
            </div>
            <div className="flex flex-wrap gap-3 lg:flex-col lg:flex-nowrap">
              {statusOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center space-x-2 rtl:space-x-reverse cursor-pointer min-w-fit"
                >
                  <input
                    type="checkbox"
                    value={option.value}
                    {...register("status", { setValueAs: (v) => v || [] })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-sm text-gray-700 whitespace-nowrap">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="lg:w-3/4 space-y-6 rtl:space-x-reverse">
          {/* Orders Table */}
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <SortHeader
                    field="id"
                    label={t("accountantDashboard.table.orderId")}
                  />
                  <SortHeader
                    field="created_at"
                    label={t("accountantDashboard.table.date")}
                  />
                  <SortHeader
                    field="status"
                    label={t("accountantDashboard.table.paymentStatus")}
                  />
                  <SortHeader
                    field="total"
                    label={t("accountantDashboard.table.codAmount")}
                  />
                  <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pick & Pack Fee
                  </th>
                  <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Services Fee
                  </th>
                  <SortHeader
                    field="delivery_fees"
                    label={t("accountantDashboard.table.deliveryFee")}
                  />
                  <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Grand Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {isLoading
                  ? [...Array(5)].map((_, index) => (
                      <tr key={index}>
                        {[...Array(8)].map((_, colIndex) => (
                          <td key={colIndex} className="p-3">
                            <Skeleton width={80} />
                          </td>
                        ))}
                      </tr>
                    ))
                  : orders?.data.map((order: iOrder) => (
                      <tr key={order.id}>
                        <td className="p-3 font-medium">
                          {order.external_order_number || order.id}
                        </td>
                        <td className="p-3">{formatDate(order.created_at)}</td>
                        <td className="p-3">
                          <span
                            className={`text-xs px-2 py-1 text-center w-fit rounded-full
                          ${getStatusColors(order.status).bg}
                          ${getStatusColors(order.status).text}
                          `}
                          >
                            {formatStatus(order.status)}
                          </span>
                        </td>
                        <td className="p-3">JOD {order.price.toFixed(2)}</td>
                        <td className="p-3">
                          {order.pick_pack_fee !== undefined &&
                          order.pick_pack_fee > 0 ? (
                            <span className="text-blue-600">
                              JOD {order.pick_pack_fee.toFixed(3)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          {order.services_fee !== undefined &&
                          order.services_fee > 0 ? (
                            <span className="text-purple-600">
                              JOD {order.services_fee.toFixed(3)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="p-3">
                          JOD {order.delivery_fees.toFixed(2)}
                        </td>
                        <td className="p-3">
                          <span className="font-semibold text-green-700">
                            JOD {(order.grand_total || order.total).toFixed(3)}
                          </span>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-500 mb-2 sm:mb-0">
                {t("accountantDashboard.table.showing")}{" "}
                {orders?.meta.from || 0}-{orders?.meta.to || 0}{" "}
                {t("accountantDashboard.table.of")} {orders?.meta.total || 0}{" "}
                {t("accountantDashboard.table.entries")}
              </div>
              <div className="flex space-x-2">
                <button
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  {t("accountantDashboard.table.previous")}
                </button>
                {[...Array(orders?.meta.last_page || 1)].map((_, index) => (
                  <button
                    key={index}
                    className={`px-3 py-1 border rounded text-sm ${
                      currentPage === index + 1 ? "bg-blue-600 text-white" : ""
                    }`}
                    onClick={() => handlePageChange(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                  disabled={currentPage === orders?.meta.last_page}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  {t("accountantDashboard.table.next")}
                </button>
              </div>
            </div>
          </div>

          {/* Recent Payouts */}
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="text-lg font-medium mb-4">
              {t("accountantDashboard.recentPayments.title")}
            </h2>
            <div className="flex flex-col gap-2">
              {isLoading
                ? [...Array(2)].map((_, index) => (
                    <div key={index} className="border rounded-md p-4">
                      <Skeleton height={20} />
                    </div>
                  ))
                : payouts?.data.map((payout: iPayment) => (
                    <div
                      key={payout.id}
                      className="border rounded-md p-4 flex justify-between"
                    >
                      <div>
                        <div className="font-medium">
                          {t("accountantDashboard.recentPayments.payout")}
                          {payout.id}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(payout.date)}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-col items-end">
                        <div className="font-medium">
                          {" "}
                          JOD{" "}
                          {Number(payout.amount || 0).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </div>
                        <div
                          className={`text-xs px-2 py-1 text-center w-fit rounded-full  ${
                            getStatusColors(payout.status).bg
                          }
                          ${getStatusColors(payout.status).text}`}
                        >
                          {payout.status.charAt(0).toUpperCase() +
                            payout.status.slice(1)}
                        </div>
                      </div>
                    </div>
                  ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountantDashboard;
